import React, { useMemo, useState } from 'react';

const ChatbotLogsTab = ({ 
  chatbotLogs, 
  chatbotLogsLoading, 
  loadChatbotLogs
}) => {
  const [selectedLog, setSelectedLog] = useState(null);

  const safeString = (v) => (typeof v === 'string' ? v : (v == null ? '' : String(v)));
  const truncate = (v, max = 120) => {
    const s = safeString(v);
    return s.length > max ? `${s.slice(0, max)}...` : s;
  };

  const rows = useMemo(() => {
    return (Array.isArray(chatbotLogs) ? chatbotLogs : []).map((log) => {
      const details = (log?.details && typeof log.details === 'object') ? log.details : {};
      return {
        ...log,
        _details: details,
        _question: details.question || '-',
        _answerPreview: details.answerPreview || '-',
        _tools: Array.isArray(details.toolsUsed) ? details.toolsUsed.join(', ') : '-',
        _latency: typeof details.latencyMs === 'number' ? `${details.latencyMs} ms` : '-',
        _status: details.hadError ? 'Error' : 'OK'
      };
    });
  }, [chatbotLogs]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">ChatBot Logs</h2>
        <button 
          onClick={loadChatbotLogs}
          disabled={chatbotLogsLoading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded flex items-center"
        >
          <i className={`fa-solid fa-sync-alt mr-2 ${chatbotLogsLoading ? 'animate-spin' : ''}`}></i>
          {chatbotLogsLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Latest Chatbot Turns</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tools</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((log) => (
                <tr key={log.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                    <div className="font-medium truncate" title={log.userEmail}>{log.userEmail || '-'}</div>
                    <div className="text-xs text-gray-500 truncate">{[log.name, log.lastName].filter(Boolean).join(' ') || '-'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-sm">
                    <div className="truncate" title={log._question}>{truncate(log._question, 140)}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={log._tools}>{log._tools}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log._latency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${log._status === 'Error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {log._status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View details"
                    >
                      <i className="fa-solid fa-eye"></i>
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="px-6 py-6 text-sm text-gray-500" colSpan={7}>
                    No chatbot logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl border">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Chatbot Log Details</h3>
              <button onClick={() => setSelectedLog(null)} className="text-gray-500 hover:text-gray-700">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold">Timestamp:</span> {selectedLog.timestamp ? new Date(selectedLog.timestamp).toLocaleString() : '-'}</div>
                <div><span className="font-semibold">User:</span> {selectedLog.userEmail || '-'}</div>
                <div><span className="font-semibold">Action:</span> {selectedLog.action || '-'}</div>
                <div><span className="font-semibold">Latency:</span> {selectedLog._latency}</div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Question</h4>
                <pre className="bg-gray-50 border rounded p-3 text-sm whitespace-pre-wrap">{safeString(selectedLog._details?.question || '-')}</pre>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Answer Preview</h4>
                <pre className="bg-gray-50 border rounded p-3 text-sm whitespace-pre-wrap">{safeString(selectedLog._details?.answerPreview || '-')}</pre>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Raw Details</h4>
                <pre className="bg-gray-50 border rounded p-3 text-xs overflow-x-auto">
                  {JSON.stringify(selectedLog._details || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotLogsTab; 